// Panel principal de "configurar la generación". Es el render del step
// `configure` del PresetDetailSheet. Switcha el layout interno según el
// `kind` del preset:
//   • template / design (E-commerce)  → Modelo + Formato + Otra config.
//   • design-modelo / design-auto     → delega en DesignModeloPanel
//     (Modelo + Pose + Fondo + Formato + Otra config).
//
// Provee también las sub-vistas del picker de Modelo (create/upload) y
// el FormatPickerInline en desktop como vistas que ocupan el panel
// entero (panelView state machine).

import React, { useRef, useState } from 'react';
import {
  ChevronRight,
  Image as ImageIcon,
  Minus,
  Pencil,
  Play,
  Plus,
  Sparkles,
  User,
  X,
} from 'lucide-react';
import {
  ModelPickerInline,
  modelSelectionLabel,
  type ModelPickerView,
  type ModelSelection,
} from '../ModelPicker';
import { FormatPickerInline } from '../FormatPicker';
import {
  type FondoPickerView,
  type FondoSelection,
} from '../FondoPicker';
import type { PoseV2 } from '../posesApi';
import type { FondoBiblioteca, FondoGuardado } from '../fondosApi';
import type { ModelV2 } from '../modelsApi';
import CachedImg from '../../../components/CachedImg';
import {
  CONFIG_OPTIONS_V2,
  getFormatLabel,
  type MockProductV2,
} from '../mockData';
import { DesignModeloPanel } from './DesignModeloPanel';
import {
  AnuncioPanel,
  type AnuncioFieldState,
} from './AnuncioPanel';
import { GanchoSection } from '../components/GanchoSection';
import { GanchoExplainerSheet } from '../components/GanchoExplainerSheet';
import { scrollToAndShake } from '../scrollShake';
import {
  VoiceGuionSection,
  isVoiceGuionIncomplete,
  type VoiceGuionValue,
} from '../components/VoiceGuionSection';

// Tope del stepper "x imágenes" en plantillas. Mínimo siempre 1. Con más
// de 1, el Generar dispara la producción (carrusel) por las imágenes extra.
const IMAGE_COUNT_MAX = 6;

export interface ConfigurePanelProps {
  /** Lista de productos elegidos (multi-selección). Mínimo 1. */
  products: MockProductV2[];
  /**
   * Si el preset es 'design' (fila "Diseña tu imagen") mostramos un
   * layout extendido: Modelo + Fondo (2 cols) + Formato (row plana,
   * menos prominente). Si es 'template', el layout original: Modelo +
   * Formato (2 cols) + "Otra cosa" (input libre).
   */
  kind:
    | 'template'
    | 'design'
    | 'design-modelo'
    | 'design-auto'
    | 'design-producto'
    | 'catalogo-ia'
    | 'anuncio';
  /** Si false y kind === 'template', escondemos la sección de Modelo en
   *  el configure (plantillas que solo aceptan producto, no modelo).
   *  Default true para back-compat. */
  acceptsModelo?: boolean;
  /** mobile = botón Modelo abre sheet · desktop = picker inline. */
  variant: 'mobile' | 'desktop';
  modelSelection: ModelSelection | null;
  fondoVal: string | null;
  formatoVal: string | null;
  otraConfig: string;
  // ── Modo 'design-modelo' ─────────────────────────────────────────────
  allPoses: PoseV2[];
  poseIds: string[];
  onPoseIdsChange: (ids: string[]) => void;
  /** Toggle "Seleccionar múltiples" del menú de poses (vive en
   *  InterfazV2; el switch se muestra en el PosePickerSheet). Con false
   *  el picker inline reemplaza la selección en cada tap. */
  multiPoseEnabled: boolean;
  onOpenPosePicker: () => void;
  bibliotecaFondos: FondoBiblioteca[];
  fondosGuardados: FondoGuardado[];
  fondoSelection: FondoSelection | null;
  onFondoSelectionChange: (sel: FondoSelection) => void;
  onOpenFondoPicker: () => void;
  fondoView: FondoPickerView;
  onFondoViewChange: (v: FondoPickerView) => void;
  onFondoSaved: () => void;
  /**
   * Cuando true (desktop), el botón Generar se separa con `mt-auto` para
   * pegarse al bottom del panel. En mobile (bottom sheet con max-h y
   * scroll) preferimos que Generar quede al final natural del contenido.
   */
  stickyGenerate?: boolean;
  onClearProducts: () => void;
  /** "Añadir producto al outfit" — abre el picker para sumar más productos
   *  a combinar. Solo se muestra en plantilla / imagen-como-modelo. */
  onAddProducts?: () => void;
  onModelChange: (sel: ModelSelection) => void;
  // ── Modo "varios modelos" (UI-only) ─────────────────────────────
  modelSelectionsExtra: (ModelSelection | null)[];
  onModelChangeExtras: (next: (ModelSelection | null)[]) => void;
  multiModelEnabled: boolean;
  onMultiModelEnabledChange: (v: boolean) => void;
  onOpenModelPicker: () => void;
  /** Abre el ModelPickerSheet directo a una vista. Lo usa design-modelo
   *  desktop cuando el user toca "Creá tu modelo" o "Cargá modelo" en
   *  el inline picker — en vez de cambiar a inline sub-view, queremos
   *  mostrar el sheet como side panel. */
  onOpenModelSheet: (initialView: ModelPickerView) => void;
  /** Variante del anterior para el sub-flow de multi (slot pre-set). */
  onOpenModelSheetForMultiSlot: (
    slotIdx: number,
    initialView: 'create' | 'upload',
  ) => void;
  /** Productos asignados por slot (modo varios modelos). */
  multiProductsPerSlot: Record<number, MockProductV2[]>;
  /** Tap en "Utilizando"/miniaturas de un slot del MultiView. */
  onTapSlotProducts: (slotIdx: number) => void;
  /** Abre el FondoPickerSheet directo a una vista (describe / upload). */
  onOpenFondoSheet: (initialView: FondoPickerView) => void;
  onFondoChange: (v: string | null) => void;
  onFormatoChange: (v: string | null) => void;
  onOpenFormatPicker: () => void;
  onOtraChange: (v: string) => void;
  /** `cost` = créditos que consumirá esta generación (lo que muestra el pill
   *  del botón). InterfazV2 lo usa para chequear saldo SUFICIENTE, no solo
   *  disponibilidad. Ver ADR-518. */
  onGenerate: (cost: number) => void;
  /** Solo plantillas (kind 'template', modo imagen). Si está provisto,
   *  el stepper "x imágenes" puede pedir más de 1: en ese caso el Generar
   *  hace la generación normal y luego, ya en el inline de imágenes
   *  generadas, dispara la generación de carrusel. `carruselCount` es
   *  cuántas imágenes pedirle al carrusel = (imágenes elegidas − 1),
   *  porque la primera del set es la imagen base recién generada.
   *  `cost` = créditos totales de la producción (base + carrusel). */
  onGenerateProduction?: (carruselCount: number, cost: number) => void;
  /** Costo en créditos del botón "Generar producción" = generación
   *  normal + carrusel (2 imágenes). Se muestra en el pill del botón
   *  igual que el costo del Generar. */
  productionCreditCost?: number | null;
  // ── Modo 'design-auto' (cantidad de imágenes) ────────────────────
  /** Cantidad de imágenes a generar — solo se usa cuando el preset es
   *  'design-auto'. Controla el +/- arriba del Generar. */
  autoImageCount: number;
  onAutoImageCountChange: (v: number) => void;
  /** Tope superior del autoImageCount (mínimo siempre es 1). */
  autoImageCountMax: number;
  /** Modelos reales (para el ModelPickerInline desktop). */
  userModels: ModelV2[];
  userAuthId: string | null;
  userCompanyId: string | null;
  onModelCreated: () => void;
  /** Si true, esconde el botón Generar del bottom del panel — el padre
   *  lo renderiza flotante en otra esquina (e.g. design-modelo desktop). */
  hideGenerateButton?: boolean;
  /** Costo en créditos de la generación actual (computado por el padre
   *  a partir del template + multi-preset count + duración). Si está
   *  provisto, se muestra como texto debajo del botón Generar. */
  creditCost?: number | null;
  /** Si true, oculta los botones para agregar/quitar slots del multi-
   *  modelo. Lo usa el flow de E-commerce (kind: 'design') donde la
   *  pose define cuántos modelos van. */
  lockSlotCount?: boolean;
  // ── Modo video (toggle "Imagen / Video" en el header) ──────────────
  /** Cuando true, mostramos el botón "Configurar video" arriba de los
   *  pickers normales. Solo aplica al kind 'template'. */
  videoModeEnabled?: boolean;
  /** Duración seleccionada para los videos a generar (segundos). */
  videoDuration?: number;
  /** Toggle de escenas múltiples. */
  videoMultiScene?: boolean;
  /** Costo en créditos del video por cada imagen generada — solo se usa
   *  para mostrar info al user dentro del botón. */
  videoCreditCostPerImage?: number;
  /** Abre el sheet de configuración de video. */
  onOpenVideoConfig?: () => void;
  // ── Modo "Video con influencer" (dev-only) ─────────────────────────
  /** Cuando true, inyecta la sección "Gancho" (selector + explicador)
   *  como opción adicional, debajo de "Configurar video". Lo activa el
   *  modo `video-influencer`, que reusa este panel como plantilla-video. */
  showGancho?: boolean;
  /** Gancho elegido (controlado desde InterfazV2) — se manda como `gancho_id`
   *  en la generación de video-influencer. */
  ganchoId?: string | null;
  onGanchoIdChange?: (id: string | null) => void;
  /** Sección "Voz y guion" del modo video-influencer (voz + forma de hablar
   *  + guion automático/escrito). Controlado desde InterfazV2, que lo
   *  mapea al payload (`voz_id`, `estilo_voz`, `dialogo*`). ADR-525/558. */
  voiceGuion?: VoiceGuionValue;
  onVoiceGuionChange?: (next: VoiceGuionValue) => void;
  /** Setter de `videoDuration` — en el modo influencer la duración se ajusta
   *  con una barra inline dentro de "Voz y guion" (solo guion Automático),
   *  en vez del botón "Configurar duración" + sheet. ADR-528. */
  onVideoDurationChange?: (v: number) => void;
  // ── Modo 'anuncio' ──────────────────────────────────────────────────
  /** Estados de cada campo textual del modo Anuncio. Cada uno tiene
   *  `mode: 'auto' | 'custom' | 'hidden'` + `value: string`. */
  anuncioTitulo?: AnuncioFieldState;
  onAnuncioTituloChange?: (next: AnuncioFieldState) => void;
  anuncioDescripcion?: AnuncioFieldState;
  onAnuncioDescripcionChange?: (next: AnuncioFieldState) => void;
  anuncioCta?: AnuncioFieldState;
  onAnuncioCtaChange?: (next: AnuncioFieldState) => void;
  /** Hasta 4 hex strings. Si vacío, el panel cae al default
   *  `anuncioBrandColors`. */
  anuncioColors?: string[];
  onAnuncioColorsChange?: (next: string[]) => void;
  /** Paleta del `brand_profile` activo — `brand_profiles.colors`. */
  anuncioBrandColors?: string[];
}

export const ConfigurePanel: React.FC<ConfigurePanelProps> = ({
  products,
  kind,
  acceptsModelo = true,
  variant,
  modelSelection,
  formatoVal,
  otraConfig,
  stickyGenerate = false,
  onClearProducts,
  onAddProducts,
  onModelChange,
  modelSelectionsExtra,
  onModelChangeExtras,
  multiModelEnabled,
  onMultiModelEnabledChange,
  onOpenModelPicker,
  onOpenModelSheet,
  onOpenModelSheetForMultiSlot,
  multiProductsPerSlot,
  onTapSlotProducts,
  onOpenFondoSheet,
  onFormatoChange,
  onOpenFormatPicker,
  onOtraChange,
  onGenerate,
  onGenerateProduction,
  autoImageCount,
  onAutoImageCountChange,
  autoImageCountMax,
  userModels,
  userAuthId,
  userCompanyId,
  onModelCreated,
  allPoses,
  poseIds,
  onPoseIdsChange,
  multiPoseEnabled,
  onOpenPosePicker,
  bibliotecaFondos,
  fondosGuardados,
  fondoSelection,
  onFondoSelectionChange,
  onOpenFondoPicker,
  fondoView,
  onFondoViewChange,
  onFondoSaved,
  hideGenerateButton = false,
  creditCost = null,
  // productionCreditCost ya no se usa: el pill ahora muestra creditCost ×
  // cantidad de imágenes del stepper. Se mantiene en la interfaz por
  // compatibilidad con el padre, pero no se destructura.
  lockSlotCount = false,
  videoModeEnabled = false,
  videoDuration = 8,
  videoMultiScene = false,
  onOpenVideoConfig,
  showGancho = false,
  ganchoId = null,
  onGanchoIdChange,
  voiceGuion,
  onVoiceGuionChange,
  onVideoDurationChange,
  anuncioTitulo,
  onAnuncioTituloChange,
  anuncioDescripcion,
  onAnuncioDescripcionChange,
  anuncioCta,
  onAnuncioCtaChange,
  anuncioColors,
  onAnuncioColorsChange,
  anuncioBrandColors,
}) => {
  const isDesign = kind === 'design';
  const isDesignModelo = kind === 'design-modelo';
  // 'design-auto' usa el mismo layout/rama que 'design-modelo' pero
  // sin pickers de modelo/pose/fondo. `isDesignFlow` agrupa ambos para
  // los lugares donde el render es idéntico (sheet sin imagen del
  // preset, panel full-height, etc.).
  const isDesignAuto = kind === 'design-auto';
  const isDesignFlow = isDesignModelo || isDesignAuto;
  const isAnuncio = kind === 'anuncio';
  // Stepper de cantidad de imágenes — reemplaza al viejo botón "Generar
  // producción". Solo plantillas en modo imagen (mismo gating que antes).
  // Con 1 imagen el Generar hace la generación normal; con más de 1
  // dispara la producción (carrusel) igual que el botón anterior.
  const showImageCountStepper =
    kind === 'template' && !videoModeEnabled && !!onGenerateProduction;
  // Cantidad de imágenes elegida en el stepper. Local al panel — por
  // ahora >1 solo activa el envío como producción (que genera un set
  // fijo); el número todavía no parametriza cuántas imágenes pide el
  // backend.
  const [imageCount, setImageCount] = useState(1);
  // Con más de 1 imagen, el Generar usa la rama de producción; con 1, la
  // generación normal.
  const isProductionMode = showImageCountStepper && imageCount > 1;
  // Costo mostrado en el pill = 1 crédito por imagen. `creditCost` es el
  // costo de generar 1 imagen con esta plantilla, así que el total del
  // stepper es ese costo × cantidad de imágenes elegidas.
  const activeCreditCost =
    showImageCountStepper && creditCost != null
      ? creditCost * imageCount
      : creditCost;
  // Modo influencer: bloqueamos el Generar si falta el gancho (obligatorio,
  // ADR-546), si no se eligió un modelo explícito (en este modo no existe
  // "Automático") o si el guion/voz elegidos están incompletos (ADR-525/544).
  const ganchoPendiente = showGancho && !ganchoId;
  const modeloPendiente =
    showGancho && (!modelSelection || modelSelection.mode === 'auto');
  const guionPendiente =
    ganchoPendiente ||
    modeloPendiente ||
    (showGancho && !!voiceGuion && isVoiceGuionIncomplete(voiceGuion));
  // Prefijo de ids DOM para el scroll+shake del Generar bloqueado
  // (ADR-552/553) — distinto por variante porque el sheet monta ambos
  // paneles a la vez (ocultos por CSS).
  const influencerIdPrefix = `influencer-${variant === 'desktop' ? 'desktop' : 'mobile'}`;
  // Target del scroll+shake, misma prioridad que el hint: gancho → modelo →
  // voz → guion.
  const influencerMissingTargetId = ganchoPendiente
    ? `${influencerIdPrefix}-gancho-card`
    : modeloPendiente
      ? `${influencerIdPrefix}-modelo-card`
      : voiceGuion && voiceGuion.guionMode !== 'auto' && voiceGuion.vozId == null
        ? `${influencerIdPrefix}-voz-row`
        : `${influencerIdPrefix}-guion-block`;
  // Mensaje del hint (misma prioridad que el target). El último se conserva
  // en un ref para que el texto no se corte seco durante el colapso animado
  // del container (mismo patrón que el pill desktop, ADR-554).
  const influencerHintMsg = ganchoPendiente
    ? 'Elegí un gancho para generar.'
    : modeloPendiente
      ? 'Elegí un modelo para generar.'
      : voiceGuion && voiceGuion.guionMode !== 'auto' && voiceGuion.vozId == null
        ? 'Elegí una voz para generar.'
        : voiceGuion?.guionMode === 'audio'
          ? 'Grabá tu guion (o volvé a "Automático") para generar.'
          : 'Escribí tu guion (o volvé a "Automático") para generar.';
  const lastInfluencerHintRef = useRef<string>('');
  if (guionPendiente) lastInfluencerHintRef.current = influencerHintMsg;
  const handleGenerateClick = () => {
    // Costo que se le pasa al guard de créditos = exactamente lo que muestra
    // el pill (`activeCreditCost`). Fallback a 1 si no se pudo computar
    // (nunca es gratis). Ver ADR-518.
    const cost = activeCreditCost != null && activeCreditCost > 0
      ? activeCreditCost
      : 1;
    // Producción: le pedimos al carrusel (imageCount − 1) imágenes, ya que
    // la primera del set es la imagen base que genera el flujo normal.
    if (isProductionMode) onGenerateProduction?.(imageCount - 1, cost);
    else onGenerate(cost);
  };
  const isDesktop = variant === 'desktop';
  // Solo plantillas (kind 'template') pueden no aceptar modelo. Para
  // design/design-modelo siempre se muestra. Si la plantilla declara
  // `inputs_aceptados` sin "modelo", escondemos la sección.
  // 'design-producto' (vistas solo-producto de "Fondo blanco") nunca
  // lleva modelo: mismo layout que una plantilla sin modelo.
  const hideModelo =
    (kind === 'template' && !acceptsModelo) || kind === 'design-producto';
  // En el modo influencer no existe "Automático": mientras no haya un modelo
  // elegido, el botón muestra un placeholder que invita a elegir.
  const modelLabel =
    showGancho && (!modelSelection || modelSelection.mode === 'auto')
      ? 'Elegir modelo'
      : modelSelectionLabel(modelSelection);
  const formatoLabel = getFormatLabel(formatoVal);
  // Lookup defensivo del config option de fondo — no se renderiza acá
  // pero mantenemos la referencia para que el lint detecte si el id
  // cambiase en mockData.
  void CONFIG_OPTIONS_V2;

  // View del panel del costado en DESKTOP. Cuando es algo distinto a
  // 'main', se esconde el resto del panel (producto chip, modelo
  // picker, fondo, etc.) y la sub-vista ocupa el panel entero — como un
  // wizard. En mobile siempre se queda 'main' (las sub-vistas son
  // bottom sheets aparte).
  //   • 'main'         → vista normal del config panel
  //   • 'model-create' → sub-vista de modelo personalizado
  //   • 'model-upload' → sub-vista de subir foto de modelo
  //   • 'format'       → sub-vista de elegir formato
  type PanelView =
    | 'main'
    | 'model-create'
    | 'model-upload'
    | 'model-multi'
    | 'format';
  const [panelView, setPanelView] = useState<PanelView>('main');
  const desktopShowOnlyPicker = isDesktop && panelView !== 'main';

  // El ModelPickerInline tiene su propio enum (list/create/upload/multi).
  // Lo mapeamos hacia/desde el panelView para que ambos estén sincronizados.
  const modelView: ModelPickerView =
    panelView === 'model-create'
      ? 'create'
      : panelView === 'model-upload'
        ? 'upload'
        : panelView === 'model-multi'
          ? 'multi'
          : 'list';
  const handleModelViewChange = (v: ModelPickerView) => {
    if (v === 'create') setPanelView('model-create');
    else if (v === 'upload') setPanelView('model-upload');
    else if (v === 'multi') setPanelView('model-multi');
    else setPanelView('main');
  };

  // Click en el chip de Formato: en desktop tomamos el panel; en mobile
  // abrimos el bottom sheet (props del parent).
  const handleOpenFormat = () => {
    if (isDesktop) setPanelView('format');
    else onOpenFormatPicker();
  };

  // Card-sección estilo design-modelo (título + descripción a la izquierda,
  // contenido a la derecha) — la usa el layout desktop full-screen del modo
  // "Video con influencer" (ADR-533/534) para verse como "Generar imagen
  // personalizada".
  const sectionCard = (
    title: string,
    description: string,
    content: React.ReactNode,
    // Nodo opcional debajo de la descripción (ej. link "¿Qué es un gancho?").
    extra?: React.ReactNode,
  ) => (
    <section className="rounded-2xl bg-white border border-neutral-200 p-6 flex gap-6">
      <div className="w-[220px] shrink-0">
        <h3 className="text-[20px] font-semibold text-brand-black/90 leading-tight">
          {title}
        </h3>
        <p className="text-[12.5px] text-[#3C3C43]/60 mt-1.5 leading-relaxed">
          {description}
        </p>
        {extra && <div className="mt-2.5">{extra}</div>}
      </div>
      <div className="flex-1 min-w-0">{content}</div>
    </section>
  );

  // Explicador "¿Qué es un gancho?" — en la card desktop del influencer el
  // link vive en la columna de la descripción (fuera del GanchoSection), así
  // que el estado del sheet se maneja acá (ADR-535).
  const [ganchoExplainerOpen, setGanchoExplainerOpen] = useState(false);

  // Chip de producto(s) + CTA de outfit — se renderiza en el flujo estándar
  // (arriba de todo) y dentro de la card "Producto" del layout desktop del
  // modo influencer. Extraído a const para no duplicar el JSX.
  const productsChipBlock = (
    <div className="space-y-2.5">
      <div className="flex items-center gap-3 bg-[#F7F7F9] rounded-2xl p-3">
        {products.length === 1 ? (
          <CachedImg
            src={products[0].imageUrl}
            alt={products[0].name}
            className="w-12 h-12 rounded-lg object-cover shrink-0"
          />
        ) : (
          <div className="flex shrink-0 -space-x-2">
            {products.slice(0, 3).map((p) => (
              <CachedImg
                key={p.id}
                src={p.imageUrl}
                alt={p.name}
                className="w-10 h-10 rounded-lg object-cover ring-2 ring-[#F7F7F9]"
              />
            ))}
            {products.length > 3 && (
              <div className="w-10 h-10 rounded-lg bg-white ring-2 ring-[#F7F7F9] flex items-center justify-center text-[12px] font-semibold text-brand-black/70">
                +{products.length - 3}
              </div>
            )}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[14.5px] font-normal text-brand-black/90">
            {products.length > 1 ? 'Productos' : 'Producto'}
          </p>
          <p className="text-[14.5px] font-semibold text-brand-black/90 truncate">
            {products.length === 1
              ? products[0].name
              : products.map((p) => p.name).join(', ')}
          </p>
        </div>
        <button
          type="button"
          onClick={onClearProducts}
          className="w-8 h-8 inline-flex items-center justify-center rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-black/[0.04] transition-colors shrink-0"
          aria-label="Cambiar selección"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {/* CTA para sumar / editar productos del outfit — refuerza que se
          pueden combinar varios. Con un solo producto invita a sumar; con
          más de uno (ya hay un outfit) pasa a "Editar outfit". Solo en
          plantilla / imagen-como-modelo. NO en el influencer (showGancho):
          ese modo genera con un solo producto (ADR-563). */}
      {onAddProducts && (kind === 'template' || isDesignModelo) && !showGancho && (
        <button
          type="button"
          onClick={onAddProducts}
          className="w-full inline-flex items-center justify-center gap-2 py-3 px-5 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/80 hover:text-brand-primary text-[14px] font-medium transition-colors"
        >
          {products.length > 1 ? (
            <>
              <Pencil className="w-4 h-4" />
              Editar outfit
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Añadir producto al outfit
            </>
          )}
        </button>
      )}
    </div>
  );

  // ModelPickerInline (desktop) — usado en el flujo estándar y dentro de la
  // card "Modelo" del layout del modo influencer.
  const modelPickerInlineEl = (
    <ModelPickerInline
      selection={modelSelection}
      onChange={onModelChange}
      // Influencer: el modelo se elige sí o sí (sin tile "Automático").
      hideAuto={showGancho}
      view={modelView}
      onViewChange={handleModelViewChange}
      userModels={userModels}
      userId={userAuthId}
      companyId={userCompanyId}
      onModelCreated={onModelCreated}
      // Multi-mode para E-commerce poses (kind: 'design') con
      // más de 1 persona. Las plantillas tradicionales no usan
      // multi así que multiEnabled queda en false y el picker
      // sigue como single.
      selectionsExtra={modelSelectionsExtra}
      onChangeExtras={isDesign ? onModelChangeExtras : undefined}
      multiEnabled={isDesign && multiModelEnabled}
      multiProducts={products.map((p) => ({
        id: p.id,
        name: p.name,
        imageUrl: p.imageUrl,
      }))}
      multiProductsPerSlot={multiProductsPerSlot}
      onTapSlotProducts={onTapSlotProducts}
      lockSlotCount={lockSlotCount}
    />
  );

  // En desktop (`stickyGenerate = true`) el panel es `flex flex-col flex-1`
  // y el botón Generar usa `mt-auto` para pegarse al bottom. En mobile el
  // contenido fluye naturalmente sin estirar el panel.
  return (
    <div
      className={stickyGenerate ? 'flex flex-col min-h-full shrink-0' : ''}
    >
      {/* Wrapper con padding-bottom = alto de la barra Generar sticky, para
          que el ÚLTIMO control (Instrucción adicional / Configurar video) se
          pueda scrollear por ENCIMA de la barra en vez de quedar tapado.
          Cubre TODO lo que va arriba del Generar (incluido "Configurar
          video", que antes quedaba fuera). Aplica siempre que el Generar es
          STICKY (desktop, o design-flow en mobile) — antes solo desktop, por
          eso en mobile el último input quedaba tapado. En contenido corto el
          `mt-auto` del Generar lo absorbe. */}
      <div
        className={
          stickyGenerate && !desktopShowOnlyPicker && !hideGenerateButton
            ? 'pb-24'
            : ''
        }
      >
      <div className={isDesktop ? 'space-y-6' : 'space-y-5'}>
      {/* Products chip — escondido en desktop cuando el picker está en
          sub-vista (create/upload) para dejarle todo el panel a esa.
          En `design-modelo`/`design-auto` desktop también lo
          escondemos porque DesignModeloPanel lo renderiza como una
          card propia. */}
      {/* En el layout desktop del modo influencer (showGancho) el chip va
          adentro de la card "Producto" — no acá arriba. */}
      {!desktopShowOnlyPicker &&
        !(isDesignFlow && isDesktop) &&
        !isAnuncio &&
        !(showGancho && isDesktop) &&
        productsChipBlock}

      {/* Question — solo en mobile y NO en design-modelo/design-auto
          (esos tienen su propio layout de cards). En desktop el
          ModelPickerInline ocupa mucho espacio vertical y la pregunta
          queda redundante. En el modo "Video con influencer" (showGancho)
          tampoco va: el panel ya tiene su propia jerarquía (Gancho +
          opciones), la pregunta sobra. */}
      {!isDesktop && !isDesignFlow && !isAnuncio && !showGancho && (
        <p className="text-[15.5px] font-semibold text-brand-black/90">
          ¿Querés realizarle algún cambio a la configuración?
        </p>
      )}

      {isAnuncio ? (
        // ── Modo 'anuncio': AnuncioPanel renderiza productos chip + 3
        // campos textuales + grilla de colores + Formato/Otra config.
        // Props con fallback defensivo (default state + no-op handlers)
        // por si el padre olvidó pasarlas — no rompe el render.
        <AnuncioPanel
          variant={variant}
          products={products}
          onClearProducts={onClearProducts}
          titulo={anuncioTitulo ?? { hidden: false, value: '', font: '' }}
          onTituloChange={onAnuncioTituloChange ?? (() => undefined)}
          descripcion={
            anuncioDescripcion ?? { hidden: false, value: '', font: '' }
          }
          onDescripcionChange={
            onAnuncioDescripcionChange ?? (() => undefined)
          }
          cta={anuncioCta ?? { hidden: false, value: '', font: '' }}
          onCtaChange={onAnuncioCtaChange ?? (() => undefined)}
          colors={anuncioColors ?? []}
          onColorsChange={onAnuncioColorsChange ?? (() => undefined)}
          brandColors={anuncioBrandColors ?? []}
          autoImageCount={autoImageCount}
          onAutoImageCountChange={onAutoImageCountChange}
          autoImageCountMax={autoImageCountMax}
          formatoLabel={formatoLabel}
          onOpenFormat={onOpenFormatPicker}
          otraConfig={otraConfig}
          onOtraChange={onOtraChange}
          minimalHeaders="mobile"
        />
      ) : isDesignFlow ? (
        // ── Modo 'design-modelo': layout unificado con TODAS las opciones
        // (Modelo + Pose + Fondo + Formato + Otra config). Desktop usa
        // pickers inline donde aplica (Modelo y Fondo) + carrusel pose
        // con "Ver más" → sheet. Mobile usa botones que abren sheets.
        <DesignModeloPanel
          variant={variant}
          hideAutoPickers={isDesignAuto}
          autoImageCount={autoImageCount}
          onAutoImageCountChange={onAutoImageCountChange}
          autoImageCountMax={autoImageCountMax}
          products={products}
          onClearProducts={onClearProducts}
          onAddProducts={onAddProducts}
          modelSelection={modelSelection}
          onModelChange={onModelChange}
          modelSelectionsExtra={modelSelectionsExtra}
          onModelChangeExtras={onModelChangeExtras}
          multiModelEnabled={multiModelEnabled}
          onMultiModelEnabledChange={onMultiModelEnabledChange}
          onOpenModelPicker={onOpenModelPicker}
          onOpenModelSheet={onOpenModelSheet}
        onOpenModelSheetForMultiSlot={onOpenModelSheetForMultiSlot}
        multiProductsPerSlot={multiProductsPerSlot}
        onTapSlotProducts={onTapSlotProducts}
          onOpenFondoSheet={onOpenFondoSheet}
          userModels={userModels}
          userAuthId={userAuthId}
          userCompanyId={userCompanyId}
          onModelCreated={onModelCreated}
          modelView={modelView}
          onModelViewChange={handleModelViewChange}
          allPoses={allPoses}
          poseIds={poseIds}
          onPoseIdsChange={onPoseIdsChange}
          multiPoseEnabled={multiPoseEnabled}
          onOpenPosePicker={onOpenPosePicker}
          bibliotecaFondos={bibliotecaFondos}
          fondosGuardados={fondosGuardados}
          fondoSelection={fondoSelection}
          onFondoSelectionChange={onFondoSelectionChange}
          onOpenFondoPicker={onOpenFondoPicker}
          fondoView={fondoView}
          onFondoViewChange={onFondoViewChange}
          onFondoSaved={onFondoSaved}
          formatoLabel={formatoLabel}
          onOpenFormat={onOpenFormatPicker}
          otraConfig={otraConfig}
          onOtraChange={onOtraChange}
          lockSlotCount={lockSlotCount}
        />
      ) : isDesktop && panelView === 'format' ? (
        // ── Desktop format sub-view: ocupa el panel completo, mismo
        // patrón que el modelo create/upload. Tap en opción commitea y
        // vuelve al main.
        <FormatPickerInline
          selectedId={formatoVal}
          onPick={(id) => {
            onFormatoChange(id);
            setPanelView('main');
          }}
          onBack={() => setPanelView('main')}
        />
      ) : isDesktop && showGancho ? (
        // ── Desktop "Video con influencer": pantalla propia con cards
        // estilo design-modelo (título a la izquierda + contenido a la
        // derecha), sobre fondo transparente con scroll de ventana
        // (isFullScreenConfigure en PresetDetailSheet). ADR-533/534.
        desktopShowOnlyPicker ? (
          // Sub-vista del ModelPicker (create/upload) ocupa el panel entero.
          <>{!hideModelo && modelPickerInlineEl}</>
        ) : (
          <>
            {sectionCard(
              'Producto',
              'Vas a generar el video con estos productos.',
              productsChipBlock,
            )}
            {!hideModelo &&
              sectionCard(
                'Modelo',
                'La influencer de IA que muestra tu producto en el video.',
                // El id lo usa el pill flotante para scroll+shake cuando
                // falta elegir modelo (obligatorio en este modo).
                <div id={`${influencerIdPrefix}-modelo-card`}>
                  {modelPickerInlineEl}
                </div>,
              )}
            {sectionCard(
              'Planeamiento del video',
              'El gancho define la apertura del video: los primeros segundos que retienen al espectador.',
              // Carrusel horizontal de ganchos (como el picker de modelos).
              // El id lo usa el pill flotante para scroll+shake cuando falta
              // elegir gancho (ADR-552/553).
              <div id={`${influencerIdPrefix}-gancho-card`}>
                <GanchoSection
                  className=""
                  inlineCarousel
                  hideExplainer
                  selectedGanchoId={ganchoId}
                  onSelectGancho={onGanchoIdChange}
                />
              </div>,
              <button
                type="button"
                onClick={() => setGanchoExplainerOpen(true)}
                className="text-[12.5px] text-brand-primary hover:opacity-75 underline-offset-2 hover:underline transition-opacity"
              >
                ¿Qué es un gancho?
              </button>,
            )}
            <GanchoExplainerSheet
              open={ganchoExplainerOpen}
              onClose={() => setGanchoExplainerOpen(false)}
            />
            {voiceGuion && onVoiceGuionChange &&
              sectionCard(
                'Voz y guion',
                'Elegí la voz del video y decidí el guion: automático, escrito por vos o grabado con tu voz.',
                <VoiceGuionSection
                  className=""
                  companyId={userCompanyId}
                  value={voiceGuion}
                  onChange={onVoiceGuionChange}
                  videoDuration={videoDuration}
                  onVideoDurationChange={onVideoDurationChange}
                  domIdPrefix={influencerIdPrefix}
                />,
              )}
            {sectionCard(
              'Opciones adicionales',
              'Formato del video y ajustes finales.',
              <div className="flex flex-col gap-2.5">
                <button
                  type="button"
                  onClick={handleOpenFormat}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
                >
                  <span className="inline-flex items-center gap-2 text-[13.5px] text-[#3C3C43]/70">
                    <ImageIcon className="w-3.5 h-3.5" />
                    Formato
                  </span>
                  <span className="text-[13.5px] font-medium text-brand-black/85">
                    {formatoLabel}
                  </span>
                </button>
                <input
                  type="text"
                  value={otraConfig}
                  onChange={(e) => onOtraChange(e.target.value)}
                  placeholder="Instrucción adicional"
                  className="w-full h-12 px-4 rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
                />
              </div>,
            )}
          </>
        )
      ) : isDesktop ? (
        // ── Desktop: ModelPickerInline arriba + Fondo/Formato/Otra abajo
        // Cuando el picker está en create/upload, las otras secciones se
        // esconden para que ese sub-flow ocupe todo el panel del costado.
        // Si la plantilla no acepta modelo (`hideModelo`), saltamos el
        // ModelPickerInline y dejamos solo Formato + Instrucción.
        <>
          {!hideModelo && modelPickerInlineEl}
          {!desktopShowOnlyPicker && (
              // Sección "Opciones adicionales" — paralelo visual a la
              // etiqueta de Modelo arriba.
              <div className="flex flex-col gap-2.5">
                <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
                  Opciones adicionales
                </p>
                {/* E-commerce (kind 'design') usa el mismo layout que las
                    plantillas tradicionales — Formato + Instrucción adicional. */}
                <button
                  type="button"
                  onClick={handleOpenFormat}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
                >
                  <span className="inline-flex items-center gap-2 text-[13.5px] text-[#3C3C43]/70">
                    <ImageIcon className="w-3.5 h-3.5" />
                    Formato
                  </span>
                  <span className="text-[13.5px] font-medium text-brand-black/85">
                    {formatoLabel}
                  </span>
                </button>
                <input
                  type="text"
                  value={otraConfig}
                  onChange={(e) => onOtraChange(e.target.value)}
                  placeholder="Instrucción adicional"
                  className="w-full h-12 px-4 rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
                />
              </div>
            )}
        </>
      ) : isDesign ? (
        // ── Mobile + design (E-commerce): mismo layout que las
        // plantillas tradicionales — Modelo + Formato en 2 cols, con
        // "Otra cosa" abajo. NO mostramos picker de Pose porque la
        // plantilla EN SÍ ES la pose: el `poseIds` se auto-popula desde
        // el card que el user tappeó en la home (ver ecommercePoseMeta
        // effect). El fondo siempre es blanco para E-commerce.
        <>
          <div className="grid grid-cols-2 gap-2.5">
            <button
              type="button"
              onClick={onOpenModelPicker}
              className="flex flex-col items-start text-left px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors min-w-0"
            >
              <span className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
                <User className="w-3 h-3" />
                Modelo
              </span>
              <span className="text-[14.5px] font-semibold text-brand-black/90 mt-0.5 truncate w-full">
                {modelLabel}
              </span>
            </button>
            <button
              type="button"
              onClick={handleOpenFormat}
              className="flex flex-col items-start text-left px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors min-w-0"
            >
              <span className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                Formato
              </span>
              <span className="text-[14.5px] font-semibold text-brand-black/90 mt-0.5 truncate w-full">
                {formatoLabel}
              </span>
            </button>
          </div>
          <input
            type="text"
            value={otraConfig}
            onChange={(e) => onOtraChange(e.target.value)}
            placeholder="Otra cosa…"
            className="w-full h-12 px-4 rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
          />
        </>
      ) : (
        // ── Mobile + template: Modelo (sheet) + Formato 2-col, Otra cosa.
        // Si la plantilla no acepta modelo (`hideModelo`), el Modelo
        // desaparece y Formato pasa a ocupar el full-width.
        <>
          {hideModelo ? (
            <button
              type="button"
              onClick={handleOpenFormat}
              className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
            >
              <span className="inline-flex items-center gap-2 text-[13.5px] text-[#3C3C43]/70">
                <ImageIcon className="w-3.5 h-3.5" />
                Formato
              </span>
              <span className="text-[13.5px] font-medium text-brand-black/85">
                {formatoLabel}
              </span>
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              {/* El id (solo influencer) lo usa el hint del Generar para
                  scroll+shake cuando falta elegir modelo. */}
              <button
                type="button"
                id={
                  showGancho ? `${influencerIdPrefix}-modelo-card` : undefined
                }
                onClick={onOpenModelPicker}
                className="flex flex-col items-start text-left px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors min-w-0"
              >
                <span className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
                  <User className="w-3 h-3" />
                  Modelo
                </span>
                <span className="text-[14.5px] font-semibold text-brand-black/90 mt-0.5 truncate w-full">
                  {modelLabel}
                </span>
              </button>
              <button
                type="button"
                onClick={handleOpenFormat}
                className="flex flex-col items-start text-left px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors min-w-0"
              >
                <span className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" />
                  Formato
                </span>
                <span className="text-[14.5px] font-semibold text-brand-black/90 mt-0.5 truncate w-full">
                  {formatoLabel}
                </span>
              </button>
            </div>
          )}
          {/* "Otra cosa…" — en el modo "Video con influencer" (showGancho)
              este input se mueve al fondo de todo (después del Gancho) como
              "Instrucción adicional", así que acá NO va. */}
          {!showGancho && (
            <input
              type="text"
              value={otraConfig}
              onChange={(e) => onOtraChange(e.target.value)}
              placeholder="Otra cosa…"
              className="w-full h-12 px-4 rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
            />
          )}
        </>
      )}
      </div>

      {/* Configurar video — solo cuando el toggle del header está en
          'video' y el preset es 'template'. Va justo antes del Generar
          para que el user lo vea sin scrollear más. El parent abre el
          sheet de configuración (duración + escenas múltiples).
          En el modo "Video con influencer" este botón NO va (ni desktop
          ni mobile): la duración es una barra inline dentro de "Voz y
          guion", solo visible con guion Automático (ADR-528). */}
      {videoModeEnabled && onOpenVideoConfig && !showGancho && (
        <div className="mt-5">
          <button
            type="button"
            onClick={onOpenVideoConfig}
            className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
          >
            <span className="inline-flex items-center gap-2 text-[13.5px] text-[#3C3C43]/70">
              <Play className="w-3.5 h-3.5" />
              Configurar video
            </span>
            <span className="inline-flex items-center gap-2 text-[13.5px] font-medium text-brand-black/85">
              {videoDuration}s
              {videoMultiScene && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary text-[11px] font-semibold">
                  Multi
                </span>
              )}
              <ChevronRight className="w-3.5 h-3.5 text-[#3C3C43]/40" />
            </span>
          </button>
        </div>
      )}

      {/* Gancho (mobile) — opción adicional del modo "Video con influencer".
          En DESKTOP el gancho va en la card "Planeamiento del video", así que
          acá solo se renderiza en mobile. VIVE DENTRO del wrapper pb-24: si
          quedara afuera, el padding del wrapper se convierte en un hueco
          entre el contenido y estas secciones (visto en mobile, ADR-534). */}
      {showGancho && !isDesktop && (
        <div id={`${influencerIdPrefix}-gancho-card`}>
          <GanchoSection
            selectedGanchoId={ganchoId}
            onSelectGancho={onGanchoIdChange}
          />
        </div>
      )}

      {/* Voz y guion (mobile influencer) — debajo del Gancho, antes de la
          Instrucción adicional. En desktop va como card propia. ADR-525. */}
      {showGancho && !isDesktop && voiceGuion && onVoiceGuionChange && (
        <VoiceGuionSection
          companyId={userCompanyId}
          value={voiceGuion}
          onChange={onVoiceGuionChange}
          videoDuration={videoDuration}
          onVideoDurationChange={onVideoDurationChange}
          domIdPrefix={influencerIdPrefix}
        />
      )}

      {/* Instrucción adicional (mobile influencer) — última opción, abajo
          del todo: después del Gancho y justo antes del Generar. */}
      {showGancho && !isDesktop && (
        <input
          type="text"
          value={otraConfig}
          onChange={(e) => onOtraChange(e.target.value)}
          placeholder="Instrucción adicional"
          className="mt-5 w-full h-12 px-4 rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
        />
      )}
      </div>
      {/* /wrapper con padding-bottom para librar la barra Generar sticky */}

      {/* Generate button — cuando `stickyGenerate` queda pinned al
          bottom del scroll container con `position: sticky`. Los
          negative margins (`-mx-5 md:-mx-6 -mb-6`) cancelan el padding
          del wrapper padre así el bg-white llega a los bordes y al pie
          del aside; el `pt-3 pb-4 md:pb-6` re-establece el espaciado
          interno del botón. `mt-auto` mantiene el push-to-bottom para
          contenido corto (sin él, sticky arranca en la posición natural
          del flex flow — que con content chico queda flotando al medio).
          Escondido en desktop cuando el picker está en create/upload
          — esa sub-vista tiene su propio botón "Listo" o "Subir".
          También se esconde cuando `hideGenerateButton`. */}
      {!desktopShowOnlyPicker && !hideGenerateButton && (
        <div
          className={
            stickyGenerate
              ? `mt-auto sticky bottom-0 z-10 -mx-5 md:-mx-6 -mb-6 px-5 md:px-6 pt-3 pb-4 md:pb-6 bg-white/95 backdrop-blur-sm border-t border-black/[0.05]`
              : 'mt-5'
          }
        >
          {/* Stepper de cantidad de imágenes — solo plantillas en modo
              imagen. Va arriba del Generar. Con más de 1 imagen, el botón
              cambia a la rama de producción (carrusel). */}
          {showImageCountStepper && (
            <div className="mb-3 flex items-center justify-between gap-3 px-4 py-2.5 rounded-2xl bg-white border border-neutral-200">
              <span className="text-[14.5px] font-medium text-brand-black/80">
                Cantidad
              </span>
              <div className="inline-flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() =>
                    setImageCount((c) => Math.max(1, c - 1))
                  }
                  disabled={imageCount <= 1}
                  aria-label="Quitar una imagen"
                  className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-neutral-200 text-brand-black/70 hover:border-brand-primary/40 hover:text-brand-primary disabled:opacity-40 disabled:pointer-events-none transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="min-w-[88px] text-center text-[14.5px] font-semibold text-brand-black/90 tabular-nums">
                  {imageCount} {imageCount === 1 ? 'imagen' : 'imágenes'}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setImageCount((c) => Math.min(IMAGE_COUNT_MAX, c + 1))
                  }
                  disabled={imageCount >= IMAGE_COUNT_MAX}
                  aria-label="Agregar una imagen"
                  className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-neutral-200 text-brand-black/70 hover:border-brand-primary/40 hover:text-brand-primary disabled:opacity-40 disabled:pointer-events-none transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          {/* Wrapper con la card del hint DETRÁS del botón (mismo lenguaje
              que el pill flotante desktop, ADR-554): la card se alinea
              exacto a los bordes del botón y el mensaje emerge por ARRIBA
              con fade + colapso de altura. Para los modos sin hint queda
              siempre invisible. */}
          <div className="relative">
            <div
              aria-hidden
              className={`absolute inset-0 rounded-2xl bg-white/85 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] transition-opacity duration-300 ${
                guionPendiente ? 'opacity-100' : 'opacity-0'
              }`}
            />
            <div
              className={`relative z-10 overflow-hidden transition-all duration-300 ease-out ${
                guionPendiente ? 'max-h-16 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="pt-2 pb-1.5 px-4 text-center text-[12.5px] leading-snug text-[#3C3C43]/70">
                {guionPendiente
                  ? influencerHintMsg
                  : lastInfluencerHintRef.current}
              </p>
            </div>
            <div className="relative z-10">
          <button
            type="button"
            onClick={() => {
              // Bloqueado: en vez de un botón muerto, scrolleamos hasta el
              // componente faltante y lo sacudimos (ADR-552/553).
              if (guionPendiente) {
                scrollToAndShake(influencerMissingTargetId);
                return;
              }
              handleGenerateClick();
            }}
            aria-disabled={guionPendiente}
            className={`w-full inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold shadow-sm transition-colors ${
              guionPendiente ? 'opacity-60' : ''
            }`}
          >
            {videoModeEnabled ? (
              <Play className="w-5 h-5" />
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
            <span>{videoModeEnabled ? 'Generar video' : 'Generar'}</span>
            {activeCreditCost != null && activeCreditCost > 0 && (
              <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
                <img
                  src="/images/token_icon.png"
                  alt=""
                  aria-hidden="true"
                  className="w-3.5 h-3.5 object-contain"
                />
                {activeCreditCost}
              </span>
            )}
          </button>
            </div>
          </div>
          {/* /wrapper hint+botón */}
        </div>
      )}
    </div>
  );
};
